import { useQuery } from "@tanstack/react-query";
import { useApi } from "@/lib/useApi";
import { formatDate, formatEUR, label } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useRef } from "react";
import { UploadCloud, Download } from "lucide-react";
import { toast } from "sonner";

function describe(p) {
  const pre = p.product_name ? `${p.product_name} · ` : "";
  return pre + describeRule(p);
}

function describeRule(p) {
  if (p.type === "percentage") return `Percentuale ${p.config.percent}%`;
  if (p.type === "fixed") return `Importo fisso ${formatEUR(p.config.amount)} per cliente` + (Object.values(p.config.module_amounts || {}).some((v) => v) ? " + importi per modulo" : "");
  if (p.type === "tiered") return "A scaglioni: " + (p.config.tiers || []).map((t) => `[${t.from}-${t.to ?? "∞"}] → ${t.percent}%`).join(", ");
  return "—";
}

export default function ResellerPlan() {
  const api = useApi();
  const inputRef = useRef({});
  const { data, refetch } = useQuery({
    queryKey: ["reseller-plan"],
    queryFn: async () => (await api.get("/reseller/plan")).data,
  });

  const openPdf = async (id) => {
    const r = await api.get(`/files/${id}/download`, { responseType: "blob" });
    window.open(URL.createObjectURL(r.data), "_blank");
  };
  const uploadSigned = async (planId, docId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", "integrative_signed");
    fd.append("plan_id", planId);
    await api.post("/reseller/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
    toast.success("Modulo firmato caricato");
    refetch();
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Piano provvigionale</h1>
        <p className="text-sm text-slate-500 mt-1">Piani attivi, in attesa di firma e storico delle variazioni.</p>
      </div>

      {(data?.pending || []).length > 0 && (
        <div className="bg-amber-50 border-l-4 border-amber-500 p-4 text-amber-900 rounded-r-lg">
          <div className="font-semibold">Nuovo piano in attesa di firma</div>
          <div className="text-sm">Scarichi il modulo integrativo, lo firmi e lo ricarichi. Fino all'approvazione l'accesso è in sola lettura.</div>
          <div className="mt-3 space-y-3">
            {data.pending.map((p) => (
              <div key={p.id} className="bg-white border border-amber-200 rounded p-3 flex items-center justify-between flex-wrap gap-2" data-testid={`pending-plan-${p.id}`}>
                <div><div className="font-semibold">{describe(p)}</div><div className="text-xs text-slate-500">Decorrenza: {formatDate(p.effective_from)}</div></div>
                <div className="flex items-center gap-2">
                  {p.integrative_doc_id && <Button variant="outline" size="sm" onClick={() => openPdf(p.integrative_doc_id)} data-testid={`btn-dl-integ-${p.id}`}><Download size={14} className="mr-1" /> Modulo</Button>}
                  <input ref={(el) => (inputRef.current[p.id] = el)} type="file" accept="application/pdf,image/*" className="hidden"
                         onChange={(e) => e.target.files?.[0] && uploadSigned(p.id, p.integrative_doc_id, e.target.files[0])} />
                  <Button size="sm" className="bg-[#B84A2B] hover:bg-[#983C21] text-white" onClick={() => inputRef.current[p.id]?.click()} data-testid={`btn-up-integ-${p.id}`}><UploadCloud size={14} className="mr-1" /> Carica firmato</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border border-[#E2DDD5] rounded-xl p-5">
        <div className="font-semibold mb-3">Piani attivi</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-slate-500 bg-[#F2F0EB]">
              <tr><th className="text-left px-3 py-2">Dettaglio</th><th className="text-left px-3 py-2">Decorrenza</th></tr>
            </thead>
            <tbody>
              {(data?.current || []).length === 0 && <tr><td colSpan={2} className="px-3 py-6 text-center text-slate-500">Nessun piano attivo.</td></tr>}
              {(data?.current || []).map((p) => (
                <tr key={p.id} className="border-t border-[#E2DDD5]"><td className="px-3 py-2">{describe(p)}</td><td className="px-3 py-2 text-slate-500">{formatDate(p.effective_from)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white border border-[#E2DDD5] rounded-xl p-5">
        <div className="font-semibold mb-3">Storico</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-slate-500 bg-[#F2F0EB]">
              <tr><th className="text-left px-3 py-2">Dettaglio</th><th className="text-left px-3 py-2">Stato</th><th className="text-left px-3 py-2">Decorrenza</th></tr>
            </thead>
            <tbody>
              {(data?.history || []).length === 0 && <tr><td colSpan={3} className="px-3 py-6 text-center text-slate-500">Nessuna variazione passata.</td></tr>}
              {(data?.history || []).map((p) => (
                <tr key={p.id} className="border-t border-[#E2DDD5]"><td className="px-3 py-2">{describe(p)}</td><td className="px-3 py-2">{label(p.status)}</td><td className="px-3 py-2 text-slate-500">{formatDate(p.effective_from)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
